import React, { useState } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useContentToken,
  useSlotGroup,
  EditableToken,
  EditableSlotGroup,
  RichTextContent,
  useEditMode,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { cn } from '@vibesites/ui-primitives';

/**
 * FeeTransparency — OPTION 2 (Accordion), split layout
 *
 * Renders the standardized RR Living "Fee Transparency Sheet". The fee-TYPE
 * labels are identical across every RR Living property (the company's standard
 * disclosure template), so they live here in code. Only the per-property
 * AMOUNTS are editable content tokens — one stable token path per fee row —
 * which keeps the 60-property migration a deterministic field-mapping job.
 *
 * Layout: a 50/50 split — the heading, blurb, and a feature image sit in the
 * left column; the collapsible fee categories sit on the right. A "hide fees
 * that don't apply" toggle collapses away the many "—" rows. Inside the inline
 * editor every section is forced open and every row shown, so all tokens stay
 * visible and editable.
 */

// ─── Feature image slot group (defaults to the property's first gallery photo) ──
const feeImageDef: SlotGroupDefinition = {
  label: 'Feature Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[0].image' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community photo' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

const NA = '—';
function isNA(value: string | null | undefined): boolean {
  if (value == null) return true;
  const v = value.trim();
  return v === '' || v === NA;
}

type Row = { label: string; value: string | null | undefined; node: React.ReactNode };

function Chevron({ open }: { open: boolean }) {
  return (
    <svg
      aria-hidden
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={cn('h-4 w-4 shrink-0 transition-transform duration-fast', open && 'rotate-90')}
    >
      <path d="M9 6l6 6-6 6" />
    </svg>
  );
}

function FeeLine({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-6 border-t border-[color-mix(in_srgb,var(--vibe-color-text)_8%,transparent)] px-5 py-3 first:border-t-0 odd:bg-card even:bg-[color-mix(in_srgb,var(--vibe-color-text)_5%,transparent)] sm:px-6">
      <span className="font-body text-sm text-foreground">{label}</span>
      <span className="shrink-0 text-right font-body text-sm font-medium tabular-nums text-foreground">
        {children}
      </span>
    </div>
  );
}

/** A collapsible fee category. */
function FeeSection({
  title,
  rows,
  open,
  onToggle,
  showAll,
  footer,
}: {
  title: string;
  rows: Row[];
  open: boolean;
  onToggle: () => void;
  showAll: boolean;
  footer?: React.ReactNode;
}) {
  const visibleRows = showAll ? rows : rows.filter((r) => !isNA(r.value));
  const applicable = rows.filter((r) => !isNA(r.value)).length;

  return (
    <div className="overflow-hidden rounded-md border border-border border-l-2 border-l-primary bg-card shadow-sm">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left transition-colors duration-fast hover:bg-[color-mix(in_srgb,var(--vibe-color-primary)_8%,transparent)] sm:px-6"
      >
        <span className="flex items-center gap-3">
          <Chevron open={open} />
          <span className="font-heading text-sm font-normal uppercase tracking-[0.2em] text-primary">
            {title}
          </span>
        </span>
        <span className="shrink-0 font-body text-xs uppercase tracking-wider text-muted-foreground">
          {applicable} {applicable === 1 ? 'fee' : 'fees'}
        </span>
      </button>

      {/* Animated body: grid-rows 0fr→1fr gives a smooth height transition
          without measuring, paired with an opacity fade. Content stays mounted
          (collapsed by overflow-hidden) so the collapse animates too. */}
      <div
        className={cn(
          'grid transition-[grid-template-rows] duration-normal ease-in-out motion-reduce:transition-none',
          open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
        )}
        aria-hidden={!open}
      >
        <div className="overflow-hidden">
          <div
            className={cn(
              'border-t border-border transition-opacity duration-normal motion-reduce:transition-none',
              open ? 'opacity-100' : 'opacity-0',
            )}
          >
            {visibleRows.length > 0 ? (
              visibleRows.map((r) => (
                <FeeLine key={r.label} label={r.label}>
                  {r.node}
                </FeeLine>
              ))
            ) : (
              <p className="px-5 py-4 font-body text-sm italic text-muted-foreground sm:px-6">
                No fees in this category apply at this community.
              </p>
            )}
            {footer}
          </div>
        </div>
      </div>
    </div>
  );
}

export function FeeTransparency() {
  const editMode = useEditMode();
  const isEditMode = editMode?.isEditMode ?? false;

  const heading = useContentToken('feetransparency.heading');
  const featureImage = useSlotGroup('feetransparency.image', feeImageDef);

  // --- Move-In & Lease ---
  const applicationFee = useContentToken('feetransparency.application_fee');
  const administrativeFee = useContentToken('feetransparency.administrative_fee');
  const securityDeposit = useContentToken('feetransparency.security_deposit');
  const monthToMonthFee = useContentToken('feetransparency.month_to_month_fee');
  const riskFee = useContentToken('feetransparency.risk_fee');
  const moveOutCleaningFee = useContentToken('feetransparency.move_out_cleaning_fee');
  const earlyTerminationFee = useContentToken('feetransparency.early_termination_fee');
  const intraCommunityTransferFee = useContentToken('feetransparency.intra_community_transfer_fee');
  const lateFee = useContentToken('feetransparency.late_fee');
  const creditBuilder = useContentToken('feetransparency.credit_builder');
  const accessDeviceCost = useContentToken('feetransparency.access_device_cost');
  const shortTermRentalFee = useContentToken('feetransparency.short_term_rental_fee');

  // --- Utilities ---
  const electricityConnectionFee = useContentToken('feetransparency.electricity_connection_fee');
  const utilityElectric = useContentToken('feetransparency.utility_electric');
  const utilityGas = useContentToken('feetransparency.utility_gas');
  const utilityWaterSewer = useContentToken('feetransparency.utility_water_sewer');
  const utilityReimbFee = useContentToken('feetransparency.utility_reimb_fee');
  const utilitySetupFee = useContentToken('feetransparency.utility_setup_fee');
  const utilityDisconnectFee = useContentToken('feetransparency.utility_disconnect_fee');

  // --- Parking & Storage ---
  const parkingCarport = useContentToken('feetransparency.parking_carport');
  const parkingGarage = useContentToken('feetransparency.parking_garage');
  const parkingMonthly = useContentToken('feetransparency.parking_monthly');
  const parkingPass = useContentToken('feetransparency.parking_pass');
  const storageUnitFee = useContentToken('feetransparency.storage_unit_fee');

  // --- Pets ---
  const petRent = useContentToken('feetransparency.pet_rent');
  const petFee = useContentToken('feetransparency.pet_fee');

  // --- Resident Services amounts ---
  const communityAmenityFee = useContentToken('feetransparency.community_amenity_fee');
  const valetTrashFee = useContentToken('feetransparency.valet_trash_fee');
  const pestControlFee = useContentToken('feetransparency.pest_control_fee');
  const residentTechFee = useContentToken('feetransparency.resident_tech_fee');
  const trashNonValet = useContentToken('feetransparency.trash_non_valet');
  const trashReimbFee = useContentToken('feetransparency.trash_reimb_fee');
  const trashAdminFee = useContentToken('feetransparency.trash_admin_fee');
  const trashHaulingFee = useContentToken('feetransparency.trash_hauling_fee');
  const buildingLiabilityCaptive = useContentToken('feetransparency.building_liability_captive');
  const poolFee = useContentToken('feetransparency.pool_fee');
  const packageLockers = useContentToken('feetransparency.package_lockers');
  const washerDryerFee = useContentToken('feetransparency.washer_dryer_fee');
  const residentServicesTotal = useContentToken('feetransparency.resident_services_total');

  // "Hide fees that don't apply" — default on for prospects, always off (show
  // everything) while editing so every token stays visible and editable.
  const [hideNA, setHideNA] = useState(true);
  const showAll = isEditMode || !hideNA;

  // Which sections are expanded. First section opens by default; the editor
  // forces them all open.
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    moveIn: true,
  });
  const toggle = (id: string) =>
    setOpenSections((prev) => ({ ...prev, [id]: !prev[id] }));

  const moveInRows: Row[] = [
    { label: 'Application Fee', value: applicationFee, node: <EditableToken path="feetransparency.application_fee" as="span">{applicationFee}</EditableToken> },
    { label: 'Administrative Fee', value: administrativeFee, node: <EditableToken path="feetransparency.administrative_fee" as="span">{administrativeFee}</EditableToken> },
    { label: 'Security Deposit', value: securityDeposit, node: <EditableToken path="feetransparency.security_deposit" as="span">{securityDeposit}</EditableToken> },
    { label: 'Month-to-Month Fee', value: monthToMonthFee, node: <EditableToken path="feetransparency.month_to_month_fee" as="span">{monthToMonthFee}</EditableToken> },
    { label: 'Non-Refundable Risk Fee (Move-in Condition Fee)', value: riskFee, node: <EditableToken path="feetransparency.risk_fee" as="span">{riskFee}</EditableToken> },
    { label: 'Move-out Cleaning Fee', value: moveOutCleaningFee, node: <EditableToken path="feetransparency.move_out_cleaning_fee" as="span">{moveOutCleaningFee}</EditableToken> },
    { label: 'Early Termination / Insufficient Notice Fee', value: earlyTerminationFee, node: <EditableToken path="feetransparency.early_termination_fee" as="span">{earlyTerminationFee}</EditableToken> },
    { label: 'Intra-Community Transfer Fee', value: intraCommunityTransferFee, node: <EditableToken path="feetransparency.intra_community_transfer_fee" as="span">{intraCommunityTransferFee}</EditableToken> },
    { label: 'Late Fee', value: lateFee, node: <EditableToken path="feetransparency.late_fee" as="span">{lateFee}</EditableToken> },
    { label: 'Credit Builder', value: creditBuilder, node: <EditableToken path="feetransparency.credit_builder" as="span">{creditBuilder}</EditableToken> },
    { label: 'Access Device Cost', value: accessDeviceCost, node: <EditableToken path="feetransparency.access_device_cost" as="span">{accessDeviceCost}</EditableToken> },
    { label: 'Short-Term Rental Fee', value: shortTermRentalFee, node: <EditableToken path="feetransparency.short_term_rental_fee" as="span">{shortTermRentalFee}</EditableToken> },
  ];

  const utilityRows: Row[] = [
    { label: 'Utility — Electric', value: utilityElectric, node: <EditableToken path="feetransparency.utility_electric" as="span">{utilityElectric}</EditableToken> },
    { label: 'Utility — Gas', value: utilityGas, node: <EditableToken path="feetransparency.utility_gas" as="span">{utilityGas}</EditableToken> },
    { label: 'Utility — Water/Sewer', value: utilityWaterSewer, node: <EditableToken path="feetransparency.utility_water_sewer" as="span">{utilityWaterSewer}</EditableToken> },
    { label: 'Electricity Connection Fee', value: electricityConnectionFee, node: <EditableToken path="feetransparency.electricity_connection_fee" as="span">{electricityConnectionFee}</EditableToken> },
    { label: 'Utility Reimbursement Fee', value: utilityReimbFee, node: <EditableToken path="feetransparency.utility_reimb_fee" as="span">{utilityReimbFee}</EditableToken> },
    { label: 'Utility Set-up Fee', value: utilitySetupFee, node: <EditableToken path="feetransparency.utility_setup_fee" as="span">{utilitySetupFee}</EditableToken> },
    { label: 'Utility Disconnect Fee', value: utilityDisconnectFee, node: <EditableToken path="feetransparency.utility_disconnect_fee" as="span">{utilityDisconnectFee}</EditableToken> },
  ];

  const parkingRows: Row[] = [
    { label: 'Parking Fee — Carport', value: parkingCarport, node: <EditableToken path="feetransparency.parking_carport" as="span">{parkingCarport}</EditableToken> },
    { label: 'Parking Fee — Garage', value: parkingGarage, node: <EditableToken path="feetransparency.parking_garage" as="span">{parkingGarage}</EditableToken> },
    { label: 'Parking Fee — Monthly', value: parkingMonthly, node: <EditableToken path="feetransparency.parking_monthly" as="span">{parkingMonthly}</EditableToken> },
    { label: 'Parking Pass', value: parkingPass, node: <EditableToken path="feetransparency.parking_pass" as="span">{parkingPass}</EditableToken> },
    { label: 'Storage Unit Fee', value: storageUnitFee, node: <EditableToken path="feetransparency.storage_unit_fee" as="span">{storageUnitFee}</EditableToken> },
  ];

  const petRows: Row[] = [
    { label: 'Pet Rent', value: petRent, node: <EditableToken path="feetransparency.pet_rent" as="span">{petRent}</EditableToken> },
    { label: 'Pet Fee', value: petFee, node: <EditableToken path="feetransparency.pet_fee" as="span">{petFee}</EditableToken> },
  ];

  const residentServiceRows: Row[] = [
    { label: 'Community Amenity Fee', value: communityAmenityFee, node: <EditableToken path="feetransparency.community_amenity_fee" as="span">{communityAmenityFee}</EditableToken> },
    { label: 'Valet Trash Fee', value: valetTrashFee, node: <EditableToken path="feetransparency.valet_trash_fee" as="span">{valetTrashFee}</EditableToken> },
    { label: 'Pest Control Fee', value: pestControlFee, node: <EditableToken path="feetransparency.pest_control_fee" as="span">{pestControlFee}</EditableToken> },
    { label: 'Resident Tech Fee', value: residentTechFee, node: <EditableToken path="feetransparency.resident_tech_fee" as="span">{residentTechFee}</EditableToken> },
    { label: 'Trash Fee — Non-Valet', value: trashNonValet, node: <EditableToken path="feetransparency.trash_non_valet" as="span">{trashNonValet}</EditableToken> },
    { label: 'Trash Reimbursement Fee', value: trashReimbFee, node: <EditableToken path="feetransparency.trash_reimb_fee" as="span">{trashReimbFee}</EditableToken> },
    { label: 'Trash Administrative Fee', value: trashAdminFee, node: <EditableToken path="feetransparency.trash_admin_fee" as="span">{trashAdminFee}</EditableToken> },
    { label: 'Trash Hauling Fee', value: trashHaulingFee, node: <EditableToken path="feetransparency.trash_hauling_fee" as="span">{trashHaulingFee}</EditableToken> },
    { label: 'Building Liability Captive', value: buildingLiabilityCaptive, node: <EditableToken path="feetransparency.building_liability_captive" as="span">{buildingLiabilityCaptive}</EditableToken> },
    { label: 'Pool Fee', value: poolFee, node: <EditableToken path="feetransparency.pool_fee" as="span">{poolFee}</EditableToken> },
    { label: 'Package Lockers', value: packageLockers, node: <EditableToken path="feetransparency.package_lockers" as="span">{packageLockers}</EditableToken> },
    { label: 'Washer/Dryer Fee', value: washerDryerFee, node: <EditableToken path="feetransparency.washer_dryer_fee" as="span">{washerDryerFee}</EditableToken> },
  ];

  const residentServicesFooter = (
    <div className="flex items-baseline justify-between gap-6 border-t-2 border-primary bg-accent px-5 py-4 sm:px-6">
      <span className="font-heading text-sm font-normal uppercase tracking-wider text-foreground">
        Resident Services Total Per Month
      </span>
      <span className="shrink-0 text-right font-heading text-lg font-medium tabular-nums text-primary">
        <EditableToken path="feetransparency.resident_services_total" as="span">{residentServicesTotal}</EditableToken>
      </span>
    </div>
  );

  const sections: Array<{ id: string; title: string; rows: Row[]; footer?: React.ReactNode }> = [
    { id: 'moveIn', title: 'Move-In & Lease', rows: moveInRows },
    { id: 'utilities', title: 'Utilities', rows: utilityRows },
    { id: 'parking', title: 'Parking & Storage', rows: parkingRows },
    { id: 'pets', title: 'Pets', rows: petRows },
    { id: 'residentServices', title: 'Resident Services', rows: residentServiceRows, footer: residentServicesFooter },
  ];

  // When fees that don't apply are hidden, also drop entire categories that
  // have no applicable fees at all. While showing everything (edit mode or the
  // toggle off) every section stays visible so all tokens remain editable.
  const sectionHasApplicableFees = (rows: Row[]) => rows.some((r) => !isNA(r.value));
  const visibleSections = showAll
    ? sections
    : sections.filter((s) => sectionHasApplicableFees(s.rows));

  return (
    <section className="bg-background py-16 md:py-24">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <div className="grid gap-10 lg:grid-cols-2 lg:items-start lg:gap-12">
          {/* LEFT: heading, blurb, feature image */}
          <div className="lg:sticky lg:top-24">
            <EditableToken
              path="feetransparency.heading"
              as="h2"
              className="mb-4 font-heading text-3xl font-normal uppercase tracking-wide text-foreground sm:text-4xl"
            >
              {heading}
            </EditableToken>
            <span aria-hidden className="mb-6 block h-px w-16 bg-primary" />
            <RichTextContent
              path="feetransparency.intro"
              as="div"
              defaultValue="<p>The fees below apply to this community. Amounts shown are the most current figures on file and are subject to change. &ldquo;&mdash;&rdquo; indicates a fee that does not apply at this property. Please contact the leasing office for full details.</p>"
              className="font-body text-base leading-relaxed text-foreground [&_a]:text-primary [&_a]:underline [&_p]:mb-3"
            />

            <div className="mt-8 overflow-hidden rounded-md border border-border border-l-2 border-l-primary shadow-sm">
              <EditableSlotGroup groupPath="feetransparency.image" definition={feeImageDef}>
                <div className="aspect-[4/3] w-full overflow-hidden bg-muted">
                  {featureImage.image ? (
                    <VibesitesImage
                      url={featureImage.image}
                      alt={featureImage.alt || 'Community photo'}
                      className="h-full w-full object-cover"
                      sizes="(min-width: 1024px) 50vw, 100vw"
                    />
                  ) : null}
                </div>
              </EditableSlotGroup>
            </div>
          </div>

          {/* RIGHT: accordion */}
          <div>
            {!isEditMode && (
              <div className="mb-4 flex justify-end">
                <button
                  type="button"
                  role="switch"
                  aria-checked={hideNA}
                  onClick={() => setHideNA((v) => !v)}
                  className="inline-flex items-center gap-3 font-body text-xs uppercase tracking-wider text-muted-foreground transition-colors duration-fast hover:text-foreground"
                >
                  <span
                    className={cn(
                      'relative h-5 w-9 rounded-full border border-border transition-colors duration-fast',
                      hideNA ? 'bg-primary' : 'bg-muted',
                    )}
                  >
                    <span
                      className={cn(
                        'absolute top-0.5 h-3.5 w-3.5 rounded-full bg-background shadow-sm transition-all duration-fast',
                        hideNA ? 'left-[1.125rem]' : 'left-0.5',
                      )}
                    />
                  </span>
                  Hide fees that don&rsquo;t apply
                </button>
              </div>
            )}

            <div className="space-y-4">
              {visibleSections.map((s) => (
                <FeeSection
                  key={s.id}
                  title={s.title}
                  rows={s.rows}
                  footer={s.footer}
                  open={isEditMode || !!openSections[s.id]}
                  onToggle={() => toggle(s.id)}
                  showAll={showAll}
                />
              ))}
              {visibleSections.length === 0 && (
                <p className="rounded-md border border-border border-l-2 border-l-primary bg-card px-5 py-6 font-body text-sm italic text-muted-foreground shadow-sm sm:px-6">
                  No additional fees apply at this community. Please contact the
                  leasing office for full details.
                </p>
              )}
            </div>
          </div>
        </div>

        <RichTextContent
          path="feetransparency.disclaimer"
          as="div"
          defaultValue="<p>All fees are subject to change without notice and may vary by floor plan, lease term, and qualification. This sheet is provided for transparency and does not constitute an offer to lease. Please contact the leasing office for the fees and deposits that apply to your specific home.</p>"
          className="mt-12 font-body text-xs leading-relaxed text-muted-foreground [&_a]:text-primary [&_a]:underline [&_p]:mb-2"
        />
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'FeeTransparency',
  displayName: 'Fee Transparency Table',
  tokens: {
    'feetransparency.heading': {
      label: 'Heading',
      type: 'text',
      defaultValue: 'Fee Transparency',
    },
    'feetransparency.intro': {
      label: 'Intro Text',
      type: 'richtext',
      defaultValue: '<p>The fees below apply to this community. Amounts shown are the most current figures on file and are subject to change. &ldquo;&mdash;&rdquo; indicates a fee that does not apply at this property. Please contact the leasing office for full details.</p>',
    },

    'feetransparency.application_fee': { label: 'Application Fee', type: 'text', defaultField: 'property_application_fee', defaultValue: '—' },
    'feetransparency.administrative_fee': { label: 'Administrative Fee', type: 'text', defaultField: 'property_admin_fee', defaultValue: '—' },
    'feetransparency.security_deposit': { label: 'Security Deposit', type: 'text', defaultValue: '—' },
    'feetransparency.month_to_month_fee': { label: 'Month-to-Month Fee', type: 'text', defaultValue: '—' },
    'feetransparency.risk_fee': { label: 'Non-Refundable Risk Fee (Move-in Condition Fee)', type: 'text', defaultValue: '—' },
    'feetransparency.move_out_cleaning_fee': { label: 'Move-out Cleaning Fee', type: 'text', defaultValue: '—' },
    'feetransparency.electricity_connection_fee': { label: 'Electricity Connection Fee', type: 'text', defaultValue: '—' },
    'feetransparency.utility_electric': { label: 'Utility — Electric', type: 'text', defaultValue: '—' },
    'feetransparency.utility_gas': { label: 'Utility — Gas', type: 'text', defaultValue: '—' },
    'feetransparency.utility_water_sewer': { label: 'Utility — Water/Sewer', type: 'text', defaultValue: '—' },
    'feetransparency.utility_reimb_fee': { label: 'Utility Reimbursement Fee', type: 'text', defaultValue: '—' },
    'feetransparency.utility_setup_fee': { label: 'Utility Set-up Fee', type: 'text', defaultValue: '—' },
    'feetransparency.utility_disconnect_fee': { label: 'Utility Disconnect Fee', type: 'text', defaultValue: '—' },
    'feetransparency.parking_carport': { label: 'Parking Fee — Carport', type: 'text', defaultValue: '—' },
    'feetransparency.parking_garage': { label: 'Parking Fee — Garage', type: 'text', defaultValue: '—' },
    'feetransparency.parking_monthly': { label: 'Parking Fee — Monthly', type: 'text', defaultValue: '—' },
    'feetransparency.parking_pass': { label: 'Parking Pass', type: 'text', defaultValue: '—' },
    'feetransparency.storage_unit_fee': { label: 'Storage Unit Fee', type: 'text', defaultValue: '—' },
    'feetransparency.short_term_rental_fee': { label: 'Short-Term Rental Fee', type: 'text', defaultValue: '—' },
    'feetransparency.access_device_cost': { label: 'Access Device Cost', type: 'text', defaultValue: '—' },
    'feetransparency.early_termination_fee': { label: 'Early Termination / Insufficient Notice Fee', type: 'text', defaultValue: '—' },
    'feetransparency.intra_community_transfer_fee': { label: 'Intra-Community Transfer Fee', type: 'text', defaultValue: '—' },
    'feetransparency.late_fee': { label: 'Late Fee', type: 'text', defaultValue: '—' },
    'feetransparency.credit_builder': { label: 'Credit Builder', type: 'text', defaultValue: '—' },
    'feetransparency.pet_rent': { label: 'Pet Rent', type: 'text', defaultField: 'property_monthly_pet_rent', defaultValue: '—' },
    'feetransparency.pet_fee': { label: 'Pet Fee', type: 'text', defaultField: 'property_upfront_pet_fee', defaultValue: '—' },

    'feetransparency.community_amenity_fee': { label: 'Community Amenity Fee', type: 'text', defaultValue: '—' },
    'feetransparency.valet_trash_fee': { label: 'Valet Trash Fee', type: 'text', defaultValue: '—' },
    'feetransparency.pest_control_fee': { label: 'Pest Control Fee', type: 'text', defaultValue: '—' },
    'feetransparency.resident_tech_fee': { label: 'Resident Tech Fee', type: 'text', defaultValue: '—' },
    'feetransparency.trash_non_valet': { label: 'Trash Fee — Non-Valet', type: 'text', defaultValue: '—' },
    'feetransparency.trash_reimb_fee': { label: 'Trash Reimbursement Fee', type: 'text', defaultValue: '—' },
    'feetransparency.trash_admin_fee': { label: 'Trash Administrative Fee', type: 'text', defaultValue: '—' },
    'feetransparency.trash_hauling_fee': { label: 'Trash Hauling Fee', type: 'text', defaultValue: '—' },
    'feetransparency.building_liability_captive': { label: 'Building Liability Captive', type: 'text', defaultValue: '—' },
    'feetransparency.pool_fee': { label: 'Pool Fee', type: 'text', defaultValue: '—' },
    'feetransparency.package_lockers': { label: 'Package Lockers', type: 'text', defaultValue: '—' },
    'feetransparency.washer_dryer_fee': { label: 'Washer/Dryer Fee', type: 'text', defaultValue: '—' },
    'feetransparency.resident_services_total': { label: 'Resident Services Total (Per Month)', type: 'text', defaultValue: '—' },

    'feetransparency.disclaimer': {
      label: 'Disclaimer',
      type: 'richtext',
      defaultValue: '<p>All fees are subject to change without notice and may vary by floor plan, lease term, and qualification. This sheet is provided for transparency and does not constitute an offer to lease. Please contact the leasing office for the fees and deposits that apply to your specific home.</p>',
    },
  },
  slotGroups: {
    'feetransparency.image': feeImageDef,
  },
};

(FeeTransparency as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default FeeTransparency;
